<template>
  <div class="schedule-view">
    <!-- Sticky top bar -->
    <div class="top-bar">
      <!-- Search Input Overlay (visible when searchActive) -->
      <transition name="search-expand">
        <div v-if="searchActive" class="search-overlay">
          <div class="search-container">
            <button class="btn-search-back" @click="closeSearch">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            <input
              ref="searchInput"
              v-model="searchQuery"
              type="text"
              placeholder="搜尋聯賽或球隊..."
              class="search-input"
              @keyup.enter="onSearch"
            />
            <button v-if="searchQuery" class="btn-clear" @click="searchQuery = ''">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <button class="btn-search-trigger" @click="onSearch">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </button>
          </div>
        </div>
      </transition>

      <!-- Standard Top Bar Content -->
      <div v-if="!searchActive" class="top-bar__content">
        <div class="top-bar__spacer"></div>
        <h1 class="top-bar__title">
          <template v-if="favoritesMode">我的</template>
          <template v-else-if="popularMode">熱門</template>
          <template v-else-if="betsMode">我的投注</template>
          <template v-else-if="rankingMode">排行</template>
          <template v-else-if="followsMode">關注</template>
          <template v-else>賽程</template>
        </h1>
        <div class="top-bar__actions">
          <button 
            v-if="!favoritesMode && !popularMode && !betsMode"
            class="btn-action" 
            @click="openSearch" 
            title="搜尋"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Date slider (only in normal schedule mode and search is inactive) -->
    <transition name="fade">
      <date-slider
        v-if="!favoritesMode && !popularMode && !betsMode && !rankingMode && !followsMode && !searchActive"
        v-model="selectedDate"
        @change="onDateChange"
      />
    </transition>

    <!-- Content -->
    <div class="schedule-view__content" :class="{ 'has-bottom-nav': true }">
      <transition name="fade" mode="out-in">
        <!-- Search Results mode -->
        <match-list
          v-if="searchActive && hasSearched"
          key="search"
          :matches="searchResults"
          :loading="searchLoading"
          :loading-more="searchLoadingMore"
          :has-more="searchHasMore"
          :show-date="true"
          empty-title="未找到匹配賽程"
          @load-more="loadMoreSearch"
        />

        <!-- Favorites mode -->
        <match-list
          v-else-if="favoritesMode"
          key="favorites"
          :matches="favoriteMatches"
          :loading="favoritesLoading"
          :loading-more="favoritesLoadingMore"
          :has-more="favoritesHasMore"
          :show-date="true"
          empty-title="暫無關注賽程"
          empty-sub="點擊賽程左側 ☆ 加入關注"
          @load-more="loadMoreFavorites"
        />

        <!-- Popular mode -->
        <match-list
          v-else-if="popularMode"
          key="popular"
          :matches="popularMatches"
          :loading="popularLoading"
          :loading-more="popularLoadingMore"
          :has-more="popularHasMore"
          :show-date="true"
          empty-title="暫無人氣賽程"
          @load-more="loadMorePopular"
        />

        <!-- Bets mode -->
        <div class="bets-container" v-else-if="betsMode" key="bets">
          <!-- Monthly stats -->
          <div class="bets-stats" v-if="betStats">
            <div class="stat-box stat-box--win">
              <span class="stat-label">當月贏</span>
              <span class="stat-val">+{{ betStats.win_amount }}</span>
            </div>
            <div class="stat-box stat-box--total">
              <span class="stat-label">當月淨利</span>
              <span class="stat-val" :class="[betStats.net_amount > 0 ? 'text-win' : (betStats.net_amount < 0 ? 'text-lose' : 'text-void')]">
                {{ betStats.net_amount > 0 ? '+' : '' }}{{ betStats.net_amount }}
              </span>
            </div>
            <div class="stat-box stat-box--lose">
              <span class="stat-label">當月輸</span>
              <span class="stat-val">{{ betStats.lose_amount }}</span>
            </div>
          </div>

          <div class="match-list custom-scroll" @scroll="onBetScroll" v-if="betMatches.length > 0">
            <bet-card 
              v-for="group in groupedBetMatches" 
              :key="group.match.analysis_id" 
              :match="group.match"
              :bets="group.bets"
            />
            <div class="loading-more" v-if="betsLoadingMore">
              <span class="spinner"></span> 載入中...
            </div>
          </div>
          <div class="empty-state" v-else-if="!betsLoading">
            <div class="empty-icon">📝</div>
            <h3>暫無投注紀錄</h3>
          </div>
          <div class="loading-more" v-if="betsLoading && betMatches.length === 0">
            <span class="spinner"></span> 載入中...
          </div>
        </div>

        <!-- Ranking mode -->
        <div class="ranking-container list-container" v-else-if="rankingMode" key="ranking">
          <div v-if="rankingData.length > 0" class="custom-scroll">
            <ranking-card v-for="(user, idx) in rankingData" :key="user.uuid" :user="user" :rank="idx + 1" />
          </div>
          <div v-else-if="!rankingLoading" class="empty-state">
            <div class="empty-icon">🏆</div>
            <h3>暫無排行資料</h3>
          </div>
          <div class="loading-more" v-if="rankingLoading && rankingData.length === 0">
            <span class="spinner"></span> 載入中...
          </div>
        </div>

        <!-- Follows mode -->
        <div class="follows-container list-container" v-else-if="followsMode" key="follows">
          <div v-if="followsData.length > 0" class="custom-scroll">
            <follow-card v-for="user in followsData" :key="user.followed_uuid" :follow="user" />
          </div>
          <div v-else-if="!followsLoading" class="empty-state">
            <div class="empty-icon">👥</div>
            <h3>暫無關注玩家</h3>
          </div>
          <div class="loading-more" v-if="followsLoading && followsData.length === 0">
            <span class="spinner"></span> 載入中...
          </div>
        </div>

        <!-- Normal schedule mode (fallback) -->
        <match-list
          v-else
          :key="selectedDate"
          :matches="matches"
          :loading="loading"
          :loading-more="loadingMore"
          :has-more="hasMore"
          :show-date="false"
          @load-more="loadMoreMatches"
        />

      </transition>
    </div>

    <!-- Bet Modal -->
    <bet-modal 
      :show="showBetModal" 
      :match="betTargetMatch" 
      @close="closeBetModal"
      @betPlaced="onBetPlaced"
    />

    <!-- Bottom Navigation -->
    <nav class="bottom-nav">
      <div 
        class="nav-item" 
        :class="{ 'nav-item--active': popularMode }"
        @click="switchTab('popular')"
      >
        <div class="nav-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" 
                  :fill="popularMode ? 'currentColor' : 'none'"/>
          </svg>
        </div>
        <span class="nav-label">熱門</span>
      </div>

      <div 
        class="nav-item" 
        :class="{ 'nav-item--active': rankingMode }"
        @click="switchTab('ranking')"
      >
        <div class="nav-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="16 18 22 12 16 6"></polyline>
            <polyline points="8 6 2 12 8 18"></polyline>
            <line x1="12" y1="2" x2="12" y2="22"></line>
          </svg>
        </div>
        <span class="nav-label">排行</span>
      </div>

      <div 
        class="nav-item" 
        :class="{ 'nav-item--active': !popularMode && !favoritesMode && !betsMode && !rankingMode && !followsMode }"
        @click="switchTab('schedule')"
      >
        <div class="nav-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
        </div>
        <span class="nav-label">賽程</span>
      </div>

      <div 
        class="nav-item" 
        :class="{ 'nav-item--active': followsMode }"
        @click="switchTab('follows')"
      >
        <div class="nav-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
        </div>
        <span class="nav-label">關注</span>
      </div>

      <div 
        class="nav-item" 
        :class="{ 'nav-item--active': betsMode }"
        @click="switchTab('bets')"
      >
        <div class="nav-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="16"></line>
            <line x1="8" y1="12" x2="16" y2="12"></line>
          </svg>
        </div>
        <span class="nav-label">投注</span>
      </div>

      <div 
        class="nav-item" 
        :class="{ 'nav-item--active': favoritesMode }"
        @click="switchTab('favorites')"
      >
        <div class="nav-icon">
          <svg viewBox="0 0 24 24" :fill="favoritesMode ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2">
             <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26 12,2"/>
          </svg>
        </div>
        <span class="nav-label">我的</span>
      </div>
    </nav>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import { formatLocalDate } from '@/utils/date'
import DateSlider from '@/components/DateSlider.vue'
import MatchList from '@/components/MatchList.vue'
import BetModal from '@/components/BetModal.vue'
import MatchCard from '@/components/MatchCard.vue'
import BetCard from '@/components/BetCard.vue'
import RankingCard from '@/components/RankingCard.vue'
import FollowCard from '@/components/FollowCard.vue'
import { scheduleAPI } from '@/api/index'

export default {
  name: 'ScheduleView',
  components: { DateSlider, MatchList, BetModal, MatchCard, BetCard, RankingCard, FollowCard },

  data() {
    return {
      // Schedule mode
      selectedDate: formatLocalDate(new Date()),
      matches: [],
      loading: false,
      loadingMore: false,
      page: 1,
      hasMore: false,
      // Favorites mode
      favoritesLoadingMore: false,
      favoritesPage: 1,
      // Popular mode
      popularLoadingMore: false,
      popularPage: 1,
      // Bets mode
      betsLoadingMore: false,
      // Search
      searchActive: false,
      searchQuery: '',
      hasSearched: false,
      searchResults: [],
      searchLoading: false,
      searchLoadingMore: false,
      searchPage: 1,
      searchHasMore: false,
      // Bet Modal
      showBetModal: false,
      betTargetMatch: null
    }
  },

  computed: {
    ...mapGetters([
      'favoritesMode',
      'favoriteMatches',
      'favoritesHasMore',
      'favoritesLoading',
      'popularMode',
      'popularMatches',
      'popularHasMore',
      'popularLoading',
      'betsMode',
      'betMatches',
      'betStats',
      'betsHasMore',
      'betsLoading',
      'uuid',
      'rankingMode',
      'rankingData',
      'rankingLoading',
      'followsMode',
      'followsData',
      'followsLoading'
    ]),
    groupedBetMatches() {
      const map = {}
      this.betMatches.forEach(item => {
        const aid = item.match.analysis_id
        if (!map[aid]) {
          map[aid] = {
            match: item.match,
            bets: []
          }
        }
        map[aid].bets.push(item)
      })
      // Order by match date desc (most recent first)
      return Object.values(map).sort((a, b) => {
        return (b.match.match_date || "").localeCompare(a.match.match_date || "")
      })
    }
  },

  watch: {
    favoritesMode(val) {
      if (val) this.favoritesPage = 1
    },
    popularMode(val) {
      if (val) this.popularPage = 1
    },
    betsMode(val) {
      if (val) this.$store.commit('SET_BETS_DATA', { matches: [], stats: null, page: 1, hasMore: false })
    }
  },

  created() {
    // Load favorite IDs (for star state across all cards)
    this.$store.dispatch('loadFavoriteIds')
    // Load bet IDs (for "Already Bet" state across all cards)
    this.$store.dispatch('loadBetIds')
    // Load today's schedule
    this.loadSchedule(this.selectedDate, 1)
  },

  methods: {
    ...mapActions(['toggleFavoritesMode', 'loadFavoriteMatches', 'togglePopularMode', 'loadPopularMatches', 'toggleBetsMode', 'loadBetMatches']),

    switchTab(tab) {
      // Clear search when switching tabs
      this.closeSearch()

      if (tab === 'popular') {
        if (!this.popularMode) this.togglePopularMode()
      } else if (tab === 'favorites') {
        if (!this.favoritesMode) this.toggleFavoritesMode()
      } else if (tab === 'bets') {
        if (!this.betsMode) this.toggleBetsMode()
      } else if (tab === 'ranking') {
        if (!this.rankingMode) this.$store.dispatch('toggleRankingMode')
      } else if (tab === 'follows') {
        if (!this.followsMode) this.$store.dispatch('toggleFollowsMode')
      } else {
        // Schedule mode (ensure all other modes are off)
        if (this.popularMode) this.togglePopularMode()
        if (this.favoritesMode) this.toggleFavoritesMode()
        if (this.betsMode) this.toggleBetsMode()
        if (this.rankingMode) this.$store.dispatch('toggleRankingMode')
        if (this.followsMode) this.$store.dispatch('toggleFollowsMode')
      }
    },

    openBetModal(match) {
      this.betTargetMatch = match
      this.showBetModal = true
    },

    closeBetModal() {
      this.showBetModal = false
      this.betTargetMatch = null
    },

    onBetPlaced() {
      // Optional: Show toast or reload schedule partial
      alert('下注成功！')
    },

    openSearch() {
      this.searchActive = true
      this.$nextTick(() => {
        this.$refs.searchInput?.focus()
      })
    },

    closeSearch() {
      this.searchActive = false
      this.searchQuery = ''
      this.hasSearched = false
      this.searchResults = []
    },

    onSearch() {
      if (!this.searchQuery.trim()) return
      this.performSearch(1)
    },

    async performSearch(page) {
      if (page === 1) {
        this.searchLoading = true
        this.searchResults = []
        this.searchPage = 1
        this.hasSearched = true
      } else {
        this.searchLoadingMore = true
      }

      try {
        const res = await scheduleAPI.searchMatches(this.searchQuery, page, 30, this.uuid)
        const data = res.data
        if (page === 1) {
          this.searchResults = data.matches || []
        } else {
          this.searchResults = [...this.searchResults, ...(data.matches || [])]
        }
        this.searchPage = page
        this.searchHasMore = data.has_more

        // Sync betting status
        if (data.matches && data.matches.length > 0) {
          const betIds = data.matches
            .filter(m => m.is_already_bet)
            .map(m => m.analysis_id)
          if (betIds.length > 0) {
            const idsMap = {}
            betIds.forEach(id => idsMap[id] = true)
            this.$store.commit('SET_BET_MATCH_IDS', idsMap)
          }
        }
      } catch (e) {
        console.error('Failed to search', e)
      } finally {
        this.searchLoading = false
        this.searchLoadingMore = false
      }
    },

    loadMoreSearch() {
      if (!this.searchLoadingMore && this.searchHasMore) {
        this.performSearch(this.searchPage + 1)
      }
    },

    onDateChange(date) {
      this.selectedDate = date
      this.loadSchedule(date, 1)
    },

    async loadSchedule(date, page) {
      if (page === 1) {
        this.loading = true
        this.matches = []
        this.page = 1
        this.hasMore = false
      } else {
        this.loadingMore = true
      }

      try {
        const res = await scheduleAPI.getSchedule(date, page, 30, this.uuid)
        const data = res.data
        if (page === 1) {
          this.matches = data.matches || []
        } else {
          this.matches = [...this.matches, ...(data.matches || [])]
        }
        this.page = page
        this.hasMore = data.has_more

        // Sync favorite status back to store
        if (data.matches && data.matches.length > 0) {
          const favoriteIds = data.matches
            .filter(m => m.is_favorited)
            .map(m => m.analysis_id)
          if (favoriteIds.length > 0) {
            this.$store.commit('MERGE_FAVORITED_IDS', favoriteIds)
          }

          // Sync betting status
          const betIds = data.matches
            .filter(m => m.is_already_bet)
            .map(m => m.analysis_id)
          if (betIds.length > 0) {
            const idsMap = {}
            betIds.forEach(id => idsMap[id] = true)
            this.$store.commit('SET_BET_MATCH_IDS', idsMap)
          }
        }
      } catch (e) {
        console.error('Failed to load schedule', e)
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    // Provide bet event handler to match-list
    mountedBetListener() {
      this.$root.$on('bet', this.openBetModal)
    },
    beforeDestroyBetListener() {
      this.$root.$off('bet', this.openBetModal)
    },

    loadMoreMatches() {
      if (!this.loadingMore && this.hasMore) {
        this.loadSchedule(this.selectedDate, this.page + 1)
      }
    },

    async loadMoreFavorites() {
      if (!this.favoritesLoadingMore && this.favoritesHasMore) {
        this.favoritesLoadingMore = true
        await this.loadFavoriteMatches(this.favoritesPage + 1)
        this.favoritesPage += 1
        this.favoritesLoadingMore = false
      }
    },

    async loadMorePopular() {
      if (!this.popularLoadingMore && this.popularHasMore) {
        this.popularLoadingMore = true
        await this.$store.dispatch('loadPopularMatches', this.popularPage + 1)
        this.popularPage += 1
        this.popularLoadingMore = false
      }
    },

    async onBetScroll(e) {
      const el = e.target
      if (el.scrollHeight - el.scrollTop <= el.clientHeight + 100) {
        if (!this.betsLoadingMore && this.betsHasMore) {
          this.betsLoadingMore = true
          await this.loadBetMatches(this.$store.state.betsPage + 1)
          this.betsLoadingMore = false
        }
      }
    }
  },
  mounted() {
    this.$root.$on('bet', this.openBetModal)
  },
  beforeDestroy() {
    this.$root.$off('bet', this.openBetModal)
  }
}
</script>

<style scoped>
.schedule-view {
  position: relative;
  min-height: 100vh;
}

/* Top bar */
.top-bar {
  background: var(--bg-card);
  border-bottom: 1px solid var(--border);
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  height: 60px;
  width: 100%;
}

.top-bar__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  height: 100%;
}

.top-bar__spacer {
  width: 40px;
}

.top-bar__title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  text-align: center;
  margin: 0;
  flex: 1;
}

.top-bar__actions {
  display: flex;
  align-items: center;
}

.btn-action {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 12px;
  color: var(--text-primary);
  transition: var(--transition);
}

.btn-action:hover {
  background: var(--bg-overlay);
  color: var(--primary);
}

.btn-action svg {
  width: 22px;
  height: 22px;
}

/* Search Overlay */
.search-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-card);
  display: flex;
  align-items: center;
  padding: 0 12px;
  z-index: 101;
}

.search-container {
  display: flex;
  align-items: center;
  width: 100%;
  background: var(--bg-body);
  border-radius: 12px;
  padding: 4px 8px;
  border: 1px solid var(--border);
}

.search-input {
  flex: 1;
  background: none;
  border: none;
  padding: 8px 12px;
  font-size: 15px;
  color: var(--text-primary);
  outline: none;
}

.btn-search-back, .btn-clear, .btn-search-trigger {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--text-muted);
  transition: var(--transition);
}

.btn-search-back:hover, .btn-clear:hover, .btn-search-trigger:hover {
  color: var(--primary);
}

.btn-search-back svg, .btn-clear svg, .btn-search-trigger svg {
  width: 20px;
  height: 20px;
}

/* Bottom Navigation */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: var(--bg-card);
  border-top: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: space-around;
  z-index: 1000;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  height: 100%;
  gap: 4px;
  cursor: pointer;
  color: var(--text-muted);
  transition: var(--transition);
}

.nav-item--active {
  color: var(--primary);
}

/* Custom color for popular if active (Orange) */
.nav-item:nth-child(1).nav-item--active {
  color: #FF8C00;
}
.nav-item:nth-child(1).nav-item--active .nav-icon svg {
  color: #FF8C00;
}

/* Custom color for favorites if active (Gold) */
.nav-item:nth-child(3).nav-item--active {
  color: var(--gold);
}
.nav-item:nth-child(3).nav-item--active .nav-icon svg {
  color: var(--gold);
}

.nav-item--active .nav-icon {
  transform: translateY(-2px);
}

/* Custom color for Bets if active (Blue/Primary) */
.nav-item:nth-child(3).nav-item--active {
  color: var(--primary);
}

.nav-icon {
  transition: transform 0.2s ease;
}

.nav-icon svg {
  width: 22px;
  height: 22px;
}

.nav-label {
  font-size: 11px;
  font-weight: 600;
}

/* Content scroll area */
.schedule-view__content {
  padding-bottom: 20px;
}

.has-bottom-nav {
  padding-bottom: 60px; /* Space for bottom nav */
}

/* Bets mode & other list modes */
.bets-container, .list-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 120px); 
  overflow: hidden; /* Prevent parent from scrolling, children will scroll */
}

.custom-scroll {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 12px; /* Small spacing at bottom */
}

/* Special case for bets mode match-list div */
.bets-container .match-list {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 12px; /* Small spacing at bottom */
}
.padding-container {
  padding: 16px;
}
.bets-stats {
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--bg-card);
  margin-bottom: 8px;
  border-bottom: 1px solid var(--border);
}
.stat-box {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.stat-label {
  font-size: 11px;
  color: var(--text-muted);
  margin-bottom: 4px;
}
.stat-val {
  font-size: 16px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.text-win { color: #E53935; } /* Red */
.text-lose { color: #2E7D32; } /* Green */
.text-void { color: #9E9E9E; } /* Gray */
.stat-box--win .stat-val { color: #E53935; }
.stat-box--lose .stat-val { color: #2E7D32; }
.stat-box--total .stat-label { font-weight: bold; color: var(--primary); }

/* Transitions */
.search-expand-enter-active, .search-expand-leave-active {
  transition: all 0.25s ease;
}
.search-expand-enter, .search-expand-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>
